import React, { useState, useEffect } from 'react';
import {
    Card, Row, Col, Statistic, DatePicker, Select, Typography, Spin, Alert,
    Table, Tag, Divider
} from 'antd';
import {
    ShoppingOutlined,
    DollarOutlined,
    ClockCircleOutlined,
    CheckCircleOutlined,
    BarChartOutlined,
    WarningOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import api from '../../services/api';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const LabReports = () => {
    const { t } = useTranslation(['laboratory', 'common']);
    const [loading, setLoading] = useState(true);
    const [dateRange, setDateRange] = useState([
        dayjs().startOf('month'),
        dayjs().endOf('month')
    ]);
    const [currency, setCurrency] = useState('');

    // Report data
    const [ordersReport, setOrdersReport] = useState(null);
    const [revenueReport, setRevenueReport] = useState(null);
    const [turnaroundReport, setTurnaroundReport] = useState(null);
    const [pendingPayments, setPendingPayments] = useState(null);

    useEffect(() => {
        fetchAllReports();
    }, [dateRange, currency]);

    const fetchAllReports = async () => {
        try {
            setLoading(true);
            const params = {};
            if (dateRange && dateRange[0] && dateRange[1]) {
                params.startDate = dateRange[0].format('YYYY-MM-DD');
                params.endDate = dateRange[1].format('YYYY-MM-DD');
            }
            if (currency) params.currency = currency;

            const [ordersRes, revenueRes, ttRes, ppRes] = await Promise.all([
                api.get('/lab/reports/orders', { params }),
                api.get('/lab/reports/revenue', { params }),
                api.get('/lab/reports/turnaround-time', { params }),
                api.get('/lab/reports/pending-payments', { params })
            ]);

            setOrdersReport(ordersRes.data?.data || null);
            setRevenueReport(revenueRes.data?.data || null);
            setTurnaroundReport(ttRes.data?.data || null);
            setPendingPayments(ppRes.data?.data || null);
        } catch (err) {
            console.error('Error fetching reports:', err);
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Pending': return '#faad14';
            case 'In Progress': return '#1890ff';
            case 'Completed': return '#52c41a';
            case 'Delivered': return '#13c2c2';
            case 'Cancelled': return '#ff4d4f';
            default: return '#999';
        }
    };

    const pendingColumns = [
        {
            title: t('laboratory:reports.patient'),
            dataIndex: ['patientId', 'name'],
            key: 'patient',
            render: (text) => text || 'N/A'
        },
        {
            title: t('laboratory:reports.description'),
            dataIndex: 'notes',
            key: 'notes',
            render: (text) => text || '-'
        },
        {
            title: t('laboratory:reports.amount'),
            dataIndex: 'totalAmount',
            key: 'amount',
            render: (amount, record) => record.currency === 'IQD'
                ? `${amount?.toLocaleString()} IQD`
                : `$${amount?.toFixed(2)}`
        },
        {
            title: t('laboratory:reports.paid'),
            dataIndex: 'amountPaid',
            key: 'paid',
            render: (amount, record) => record.currency === 'IQD'
                ? `${amount?.toLocaleString()} IQD`
                : `$${amount?.toFixed(2)}`
        },
        {
            title: t('laboratory:reports.balanceDue'),
            dataIndex: 'balanceDue',
            key: 'balance',
            render: (amount, record) => (
                <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
                    {record.currency === 'IQD'
                        ? `${amount?.toLocaleString()} IQD`
                        : `$${amount?.toFixed(2)}`}
                </span>
            )
        },
        {
            title: t('laboratory:reports.status'),
            dataIndex: 'paymentStatus',
            key: 'status',
            render: (status) => (
                <Tag color={status === 'Pending' ? 'orange' : 'blue'}>{status}</Tag>
            )
        }
    ];

    if (loading) {
        return (
            <div style={{ textAlign: 'center', marginTop: 100 }}>
                <Spin size="large" tip={t('loading')} />
            </div>
        );
    }

    return (
        <div style={{ padding: 24 }}>
            <Title level={2}>{t('laboratory:reports.title')}</Title>

            {/* Filters */}
            <Card style={{ marginBottom: 24 }}>
                <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} sm={12} md={8}>
                        <Text strong>{t('laboratory:reports.dateRange')} </Text>
                        <RangePicker
                            value={dateRange}
                            onChange={setDateRange}
                            style={{ width: '100%', marginTop: 4 }}
                        />
                    </Col>
                    <Col xs={24} sm={8} md={4}>
                        <Text strong>{t('laboratory:reports.currency')} </Text>
                        <Select
                            placeholder={t('laboratory:reports.all')}
                            value={currency || undefined}
                            onChange={v => setCurrency(v || '')}
                            allowClear
                            style={{ width: '100%', marginTop: 4 }}
                        >
                            <Option value="USD">USD</Option>
                            <Option value="IQD">IQD</Option>
                        </Select>
                    </Col>
                </Row>
            </Card>

            {/* Orders Overview */}
            <Card title={<><BarChartOutlined /> {t('laboratory:reports.ordersOverview')}</>} style={{ marginBottom: 24 }}>
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={8}>
                        <Statistic
                            title={t('laboratory:reports.totalOrders')}
                            value={ordersReport?.totalOrders || 0}
                            prefix={<ShoppingOutlined />}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Col>
                    {ordersReport?.ordersByStatus && Object.entries(ordersReport.ordersByStatus).map(([status, count]) => (
                        <Col xs={12} sm={4} key={status}>
                            <Statistic
                                title={t(`laboratory:orders.statuses.${status}`, status)}
                                value={count}
                                valueStyle={{ color: getStatusColor(status) }}
                            />
                        </Col>
                    ))}
                </Row>

                {ordersReport?.ordersByType && Object.keys(ordersReport.ordersByType).length > 0 && (
                    <>
                        <Divider>{t('laboratory:reports.ordersByType')}</Divider>
                        <Row gutter={[16, 16]}>
                            {Object.entries(ordersReport.ordersByType).map(([type, count]) => (
                                <Col xs={12} sm={6} md={4} key={type}>
                                    <Card size="small" style={{ textAlign: 'center' }}>
                                        <Statistic title={t(`laboratory:orders.orderTypes.${type}`, type)} value={count} />
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </>
                )}
            </Card>

            {/* Revenue & Turnaround */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} md={12}>
                    <Card title={<><DollarOutlined /> {t('laboratory:reports.revenue')}</>}>
                        <Row gutter={[16, 16]}>
                            <Col span={12}>
                                <Statistic
                                    title={t('laboratory:reports.totalRevenue')}
                                    value={revenueReport?.totalRevenue || 0}
                                    prefix={<DollarOutlined />}
                                    valueStyle={{ color: '#52c41a' }}
                                    formatter={(value) => {
                                        const curr = revenueReport?.currency || 'USD';
                                        return curr === 'IQD'
                                            ? `${Number(value).toLocaleString()} IQD`
                                            : `$${Number(value).toFixed(2)}`;
                                    }}
                                />
                            </Col>
                            <Col span={12}>
                                <Statistic
                                    title={t('laboratory:reports.completedOrders')}
                                    value={revenueReport?.totalOrders || 0}
                                    prefix={<CheckCircleOutlined />}
                                    valueStyle={{ color: '#1890ff' }}
                                />
                            </Col>
                        </Row>
                    </Card>
                </Col>
                <Col xs={24} md={12}>
                    <Card title={<><ClockCircleOutlined /> {t('laboratory:reports.turnaroundTime')}</>}>
                        <Row gutter={[16, 16]}>
                            <Col span={12}>
                                <Statistic
                                    title={t('laboratory:reports.averageTurnaround')}
                                    value={turnaroundReport?.averageTurnaroundDays || 0}
                                    suffix={t('laboratory:reports.days')}
                                    valueStyle={{ color: '#722ed1' }}
                                />
                            </Col>
                            <Col span={12}>
                                <Statistic
                                    title={t('laboratory:reports.ordersMeasured')}
                                    value={turnaroundReport?.totalOrders || 0}
                                    prefix={<CheckCircleOutlined />}
                                />
                            </Col>
                        </Row>
                    </Card>
                </Col>
            </Row>

            {/* Pending Payments */}
            <Card
                title={<><WarningOutlined style={{ color: '#faad14' }} /> {t('laboratory:reports.pendingPayments')}</>}
                style={{ marginBottom: 24 }}
            >
                <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                    <Col xs={12} sm={8}>
                        <Statistic
                            title={t('laboratory:reports.pendingInvoices')}
                            value={pendingPayments?.totalInvoices || 0}
                            valueStyle={{ color: '#faad14' }}
                        />
                    </Col>
                    <Col xs={12} sm={8}>
                        <Statistic
                            title={t('laboratory:reports.totalPendingAmount')}
                            value={pendingPayments?.totalPendingAmount || 0}
                            prefix={<DollarOutlined />}
                            valueStyle={{ color: '#ff4d4f' }}
                            precision={2}
                        />
                    </Col>
                </Row>

                {pendingPayments?.invoices?.length > 0 && (
                    <Table
                        columns={pendingColumns}
                        dataSource={pendingPayments.invoices}
                        rowKey="_id"
                        pagination={{ pageSize: 10 }}
                        size="small"
                        scroll={{ x: 600 }}
                    />
                )}
            </Card>
        </div>
    );
};

export default LabReports;
